import { useEffect, useState, useCallback } from "react";
import api, { errorMessage } from "../lib/api";
import { GhostButton } from "./Primitives";
import { RefreshCw, ShieldAlert, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * RosterHealthSection
 * ────────────────────────────────────────────────────────────────
 * Roster Health widget on the Metrics tab. Shows guards trending
 * toward this US-payroll week's overtime threshold (Sun→Sat, agency
 * setting `overtime_threshold_hours`, default 40h) with side-by-side
 * scheduled vs. actual hours + a next-shift breadcrumb so schedulers
 * can rebalance the roster before payroll runs.
 *
 * Data source: `GET /api/metrics/roster-health`.
 */
function riskRowClass(level) {
  if (level === "over") return "bg-[#3B0F0F]/50 border-l-2 border-l-[#FF3B30]";
  if (level === "at_risk") return "bg-[#3B2A0F]/40 border-l-2 border-l-[#FF9F0A]";
  return "";
}

function riskBadge(level) {
  if (level === "over") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase bg-[#3B0F0F] text-[#FF3B30] border border-[#FF3B30]/40">
        <ShieldAlert className="size-3" /> OVER
      </span>
    );
  }
  if (level === "at_risk") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase bg-[#3B2A0F] text-[#FF9F0A] border border-[#FF9F0A]/40">
        <AlertTriangle className="size-3" /> AT RISK
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase bg-[#0F3B1F] text-[#34C759] border border-[#34C759]/40">
      SAFE
    </span>
  );
}

function fmtHrs(n) {
  return typeof n === "number" ? `${n.toFixed(1)}h` : "—";
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function RosterHealthSection() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/metrics/roster-health");
      setData(res.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load roster health"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = data?.rows || [];
  const visibleRows = showAll ? rows : rows.filter((r) => r.risk_level !== "safe");
  const displayRows = visibleRows.length ? visibleRows : rows.slice(0, 10);

  return (
    <div
      className="border border-[#27272A] bg-[#121212]"
      data-testid="roster-health-section"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <Users className="size-4 text-zinc-400" />
          <div className="text-sm font-mono uppercase tracking-wider">
            Roster Health
          </div>
          {data?.threshold ? (
            <span className="text-xs text-zinc-500 font-mono">
              · OT threshold {data.threshold}h/wk
            </span>
          ) : null}
          {data?.over_count != null ? (
            <span
              className="text-xs text-zinc-400 ml-2"
              data-testid="roster-health-summary"
            >
              <span className="text-[#FF3B30] font-mono">{data.over_count}</span> over ·{" "}
              <span className="text-[#FF9F0A] font-mono">{data.at_risk_count}</span> at risk
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="text-xs text-zinc-400 hover:text-white underline underline-offset-2"
            onClick={() => setShowAll((v) => !v)}
            data-testid="roster-health-toggle-all"
          >
            {showAll ? "Only at-risk" : "Show all guards"}
          </button>
          <GhostButton
            data-testid="roster-health-refresh"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </GhostButton>
        </div>
      </div>

      {loading && !data ? (
        <div className="p-8 text-center text-zinc-500 scanlines" data-testid="roster-health-loading">
          Loading roster health…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-zinc-500" data-testid="roster-health-empty">
          No shifts scheduled this week.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="roster-health-table">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-[#27272A]">
              <tr>
                <th className="text-left px-4 py-2">Risk</th>
                <th className="text-left px-4 py-2">Officer</th>
                <th className="text-right px-4 py-2">Scheduled</th>
                <th className="text-right px-4 py-2">Actual</th>
                <th className="text-right px-4 py-2">Over (sched)</th>
                <th className="text-left px-4 py-2">Next Shift</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) => (
                <tr
                  key={r.guard_id}
                  className={`border-b border-[#1F1F22] hover:bg-[#1a1a1e] ${riskRowClass(r.risk_level)}`}
                  data-testid={`roster-health-row-${r.guard_id}`}
                >
                  <td className="px-4 py-2">{riskBadge(r.risk_level)}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.guard_name}</div>
                    {r.badge_no ? (
                      <div className="text-[11px] text-zinc-500 font-mono">{r.badge_no}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {fmtHrs(r.scheduled_hours)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400">
                    {fmtHrs(r.actual_hours)}
                  </td>
                  <td
                    className={`px-4 py-2 text-right font-mono ${
                      r.scheduled_overtime > 0 ? "text-[#FF3B30]" : "text-zinc-600"
                    }`}
                  >
                    {r.scheduled_overtime > 0 ? `+${r.scheduled_overtime.toFixed(1)}h` : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-zinc-400">
                    {r.next_shift ? (
                      <>
                        <div>{fmtDateTime(r.next_shift.start)}</div>
                        <div className="text-[11px] text-zinc-500">
                          {r.next_shift.site_name || "—"}
                        </div>
                      </>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && rows.length > 0 && visibleRows.length === 0 && !showAll ? (
        <div className="px-4 py-3 text-xs text-zinc-500 border-t border-[#27272A]" data-testid="roster-health-all-safe">
          All guards are safely under the {data.threshold}h weekly threshold this week. Nice work.
        </div>
      ) : null}
    </div>
  );
}
